import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaChartLine, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const StockProfitCalculator = () => {
  const [tradeType, setTradeType] = useState('delivery'); // delivery, intraday, fno
  const [buyPrice, setBuyPrice] = useState(500);
  const [sellPrice, setSellPrice] = useState(600);
  const [quantity, setQuantity] = useState(100);
  const [result, setResult] = useState(null);

  // Brokerage rates (approximate, varies by broker)
  const brokerageRates = {
    delivery: { brokerage: 0, stt: 0.1, exchange: 0.00345, sebi: 0.0001, gst: 18, stampDuty: 0.015 },
    intraday: { brokerage: 0.03, stt: 0.025, exchange: 0.00345, sebi: 0.0001, gst: 18, stampDuty: 0.003 },
    fno: { brokerage: 20, stt: 0.05, exchange: 0.05, sebi: 0.0001, gst: 18, stampDuty: 0.003 },
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, quantity, tradeType]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const bp = Number(buyPrice) || 0;
    const sp = Number(sellPrice) || 0;
    const qty = Number(quantity) || 0;
    
    const rates = brokerageRates[tradeType];
    const buyValue = bp * qty;
    const sellValue = sp * qty;
    const turnover = buyValue + sellValue;
    
    // Calculate charges
    let brokerage;
    if (tradeType === 'fno') {
      brokerage = rates.brokerage * 2; // Flat per order
    } else {
      brokerage = (turnover * rates.brokerage) / 100;
    }
    
    const sttBuy = tradeType === 'delivery' ? (buyValue * rates.stt) / 100 : 0;
    const sttSell = (sellValue * rates.stt) / 100;
    const stt = sttBuy + sttSell;
    
    const exchangeCharges = (turnover * rates.exchange) / 100;
    const sebiCharges = (turnover * rates.sebi) / 100;
    const gst = ((brokerage + exchangeCharges) * rates.gst) / 100;
    const stampDuty = (buyValue * rates.stampDuty) / 100;
    
    const totalCharges = brokerage + stt + exchangeCharges + sebiCharges + gst + stampDuty;
    
    const grossProfit = sellValue - buyValue;
    const netProfit = grossProfit - totalCharges;
    const profitPercent = buyValue > 0 ? ((netProfit / buyValue) * 100) : 0;
    const breakEvenPrice = bp + (qty > 0 ? (totalCharges / qty) : 0);
    
    setResult({
      buyValue,
      sellValue,
      grossProfit,
      netProfit,
      profitPercent,
      totalCharges,
      brokerage,
      stt,
      exchangeCharges,
      sebiCharges,
      gst,
      stampDuty,
      breakEvenPrice,
      isProfit: netProfit >= 0,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Stock Profit/Loss Report', {
      'Trade Type': tradeType === 'delivery' ? 'Delivery' : tradeType === 'intraday' ? 'Intraday' : 'F&O',
      'Buy Price': formatCurrency(buyPrice),
      'Sell Price': formatCurrency(sellPrice),
      'Quantity': quantity,
      'Buy Value': formatCurrency(result.buyValue),
      'Sell Value': formatCurrency(result.sellValue),
      'Gross Profit/Loss': formatCurrency(result.grossProfit),
      'Total Charges': formatCurrency(result.totalCharges),
      'Net Profit/Loss': formatCurrency(result.netProfit),
      'Return %': `${result.profitPercent.toFixed(2)}%`,
      'Break-even Price': formatCurrency(result.breakEvenPrice),
    });
  };

  const handleReset = () => {
    setBuyPrice(500);
    setSellPrice(600);
    setQuantity(100);
    setTradeType('delivery');
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Stock Profit Calculator"
        description="Free Stock Profit Calculator - Calculate profit/loss on stock trades. Include brokerage and STT."
        keywords="stock profit calculator, stock profit loss calculator, share profit calculator, brokerage calculator, STT calculator, stock trading calculator"
        canonicalPath="/stock-profit"
      />
      <div className="calculator-header">
        <h1><FaChartLine style={{ color: '#10b981' }} /> Stock Profit/Loss Calculator</h1>
        <p>Calculate your stock trading profit/loss including all charges (STT, Brokerage, GST)</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Trade Type</label>
            <div className="tabs">
              <button 
                className={`tab ${tradeType === 'delivery' ? 'active' : ''}`}
                onClick={() => setTradeType('delivery')}
              >
                Delivery
              </button>
              <button 
                className={`tab ${tradeType === 'intraday' ? 'active' : ''}`}
                onClick={() => setTradeType('intraday')}
              >
                Intraday
              </button>
              <button 
                className={`tab ${tradeType === 'fno' ? 'active' : ''}`}
                onClick={() => setTradeType('fno')}
              >
                F&O
              </button>
            </div>
          </div>

          <div className="input-group">
            <label>Buy Price (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={buyPrice === '' ? '' : buyPrice}
                onChange={(e) => setBuyPrice(e.target.value === '' ? '' : Number(e.target.value))}
                min="0.01"
                step="0.05"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Sell Price (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={sellPrice === '' ? '' : sellPrice}
                onChange={(e) => setSellPrice(e.target.value === '' ? '' : Number(e.target.value))}
                min="0.01"
                step="0.05"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Quantity</label>
            <input
              type="number"
              value={quantity === '' ? '' : quantity}
              onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="1000"
                value={quantity || 0}
                onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ 
                background: result.isProfit 
                  ? 'linear-gradient(135deg, #10b981, #059669)' 
                  : 'linear-gradient(135deg, #ef4444, #dc2626)'
              }}>
                <h3>Net {result.isProfit ? 'Profit' : 'Loss'}</h3>
                <div className="amount" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  {result.isProfit ? <FaArrowUp /> : <FaArrowDown />}
                  {formatCurrency(Math.abs(result.netProfit))}
                </div>
                <small>Return: {result.profitPercent.toFixed(2)}%</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Buy Value</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.buyValue)}</span>
                </div>
                <div className="result-item" style={{ background: '#f0fdf4', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Sell Value</span>
                  <span className="value" style={{ color: '#16a34a' }}>{formatCurrency(result.sellValue)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Gross Profit/Loss</span>
                  <span className="value" style={{ color: result.grossProfit >= 0 ? '#10b981' : '#ef4444' }}>
                    {formatCurrency(result.grossProfit)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Total Charges</span>
                  <span className="value" style={{ color: '#ef4444' }}>-{formatCurrency(result.totalCharges)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Break-even Sell Price</span>
                  <span className="value">{formatCurrency(result.breakEvenPrice)}</span>
                </div>
              </div>

              {/* Charges Breakdown */}
              <div className="breakup-section">
                <h4>Charges Breakdown</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Charge Type</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Brokerage</td>
                      <td>{formatCurrency(result.brokerage)}</td>
                    </tr>
                    <tr>
                      <td>STT (Securities Transaction Tax)</td>
                      <td>{formatCurrency(result.stt)}</td>
                    </tr>
                    <tr>
                      <td>Exchange Transaction Charges</td>
                      <td>{formatCurrency(result.exchangeCharges)}</td>
                    </tr>
                    <tr>
                      <td>SEBI Charges</td>
                      <td>{formatCurrency(result.sebiCharges)}</td>
                    </tr>
                    <tr>
                      <td>GST (18%)</td>
                      <td>{formatCurrency(result.gst)}</td>
                    </tr>
                    <tr>
                      <td>Stamp Duty</td>
                      <td>{formatCurrency(result.stampDuty)}</td>
                    </tr>
                    <tr style={{ background: '#fef2f2', fontWeight: 'bold' }}>
                      <td>Total Charges</td>
                      <td style={{ color: '#dc2626' }}>{formatCurrency(result.totalCharges)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Trading Charges in India</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li><strong>STT:</strong> 0.1% on delivery, 0.025% on intraday (sell)</li>
              <li><strong>Brokerage:</strong> Varies by broker (₹0-20 or %)</li>
              <li><strong>Exchange Charges:</strong> ~0.00345% of turnover</li>
            </ul>
          </div>
          <div>
            <ul>
              <li><strong>GST:</strong> 18% on brokerage + exchange charges</li>
              <li><strong>SEBI Charges:</strong> ₹10 per crore</li>
              <li><strong>Stamp Duty:</strong> State-wise (0.003%-0.015%)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockProfitCalculator;
